import { Star } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import { BROWSE_STAGE_LABELS, overBudgetLabel, type ApplicationStage, type CandidateBrowseRow, type CandidateTag } from '@nest-recruit/shared';
import { Stars } from '../../../../components/Stars';
import { DUPLICATE_FLAG_TONES, STAGE_TONES } from '../../../../components/status-tones';
import { ToneChip } from '../../../../components/Tone';
import { BROWSE_STAGE_TONES, overBudgetTitle } from './list-format';
import styles from './RowBits.module.css';

/** What every row and card can do; the page owns the address, so it owns these. */
export interface RowHandlers {
  readonly onOpen: (row: CandidateBrowseRow, options?: { readonly tab?: 'activity'; readonly log?: 'email' | 'call' | 'note' }) => void;
  readonly onToggleStar: (row: CandidateBrowseRow) => void;
  /** §L23 Candidate edit: tags, notes, emails and the CV Bank. */
  readonly canEdit: boolean;
  readonly stageLabels: Readonly<Record<ApplicationStage, string>>;
}

/** A plain left click opens the pop-up; any other click is the browser's, so a new tab still gets the full profile. */
export function NameLink({ row, handlers, className }: { readonly row: CandidateBrowseRow; readonly handlers: RowHandlers; readonly className?: string }): ReactNode {
  const onClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    handlers.onOpen(row);
  };
  return (
    <a href={`/candidates/${row.candidate.id}`} className={className ?? styles.name} onClick={onClick}>
      {row.candidate.fullName}
    </a>
  );
}

export function StarButton({ row, handlers }: { readonly row: CandidateBrowseRow; readonly handlers: RowHandlers }): ReactNode {
  const name = row.candidate.fullName;
  return (
    <button
      type="button"
      className={styles.star}
      data-on={row.isStarred || undefined}
      aria-pressed={row.isStarred}
      aria-label={row.isStarred ? `Remove the star from ${name}` : `Star ${name}`}
      title={row.isStarred ? 'Starred by you' : 'Star'}
      onClick={() => handlers.onToggleStar(row)}
    >
      <Star size={15} aria-hidden />
    </button>
  );
}

/** Do not rehire and Possible duplicate, under the name. */
export function PersonFlags({ row }: { readonly row: CandidateBrowseRow }): ReactNode {
  if (!row.candidate.doNotRehire && !row.hasOpenDuplicateFlag && !row.isAnswerMatch) return null;
  return (
    <span className={styles.flags}>
      {row.candidate.doNotRehire && <ToneChip tone="rejected">Do not rehire</ToneChip>}
      {row.hasOpenDuplicateFlag && <ToneChip tone={DUPLICATE_FLAG_TONES.open}>Possible duplicate</ToneChip>}
      {row.isAnswerMatch && <span className={styles.answer}>Found in an application answer</span>}
    </span>
  );
}

/** The focus application's place: a tile's stage, or the pipeline's own word for one that is out or on hold. */
export function StageChip({ row, handlers }: { readonly row: CandidateBrowseRow; readonly handlers: RowHandlers }): ReactNode {
  const focus = row.focus;
  if (focus === null) return null;
  if (focus.browseStage !== null) return <ToneChip tone={BROWSE_STAGE_TONES[focus.browseStage]}>{BROWSE_STAGE_LABELS[focus.browseStage]}</ToneChip>;
  return <ToneChip tone={STAGE_TONES[focus.stage]}>{handlers.stageLabels[focus.stage]}</ToneChip>;
}

export function stageText(row: CandidateBrowseRow, handlers: RowHandlers): string | null {
  const focus = row.focus;
  if (focus === null) return null;
  const stage = focus.browseStage !== null ? BROWSE_STAGE_LABELS[focus.browseStage] : handlers.stageLabels[focus.stage];
  return focus.stepDetail === null ? stage : `${stage} · ${focus.stepDetail}`;
}

/** The red "N% over budget" mark; the salary is masked for a viewer who may not see it, so is this. */
export function OverBudgetChip({ row }: { readonly row: CandidateBrowseRow }): ReactNode {
  const percent = row.candidate.expectedSalaryOverBudgetPercent;
  if (percent === null) return null;
  return (
    <span className={styles.over} title={overBudgetTitle(row) ?? undefined}>
      <ToneChip tone="urgent">{overBudgetLabel(percent)}</ToneChip>
    </span>
  );
}

export function Score({ row }: { readonly row: CandidateBrowseRow }): ReactNode {
  const score = row.focus?.matchScore ?? null;
  if (score === null && (row.focus?.stars ?? null) === null) return null;
  return (
    <span className={styles.score}>
      <span className={styles.scoreNumber}>{score ?? '—'}</span>
      <Stars value={row.focus?.stars ?? null} />
    </span>
  );
}

export function TagList({ tags, max }: { readonly tags: readonly CandidateTag[]; readonly max?: number }): ReactNode {
  if (tags.length === 0) return null;
  const shown = max === undefined ? tags : tags.slice(0, max);
  const hidden = tags.length - shown.length;
  return (
    <span className={styles.tags}>
      {shown.map((tag) => (
        <span key={tag.id} className={styles.tag}>
          {tag.name}
        </span>
      ))}
      {hidden > 0 && (
        <span className={styles.tag} title={tags.slice(shown.length).map((tag) => tag.name).join(', ')}>
          +{hidden}
        </span>
      )}
    </span>
  );
}
